// QR Code com o capelo do Simulador no centro. Retorna SVG string pronto pra
// dangerouslySetInnerHTML. Usa error correction H (30%) pra tolerar a obstrução
// do logo no centro (~22% da área).

import qrcode from "qrcode";

export interface QrWithLogoOptions {
  /** Margem em "quiet zone modules" ao redor do QR. Default 1. */
  margin?: number;
  /** Cor dos módulos escuros. Default "#0f172a" (slate-900). */
  dark?: string;
  /** Cor de fundo (módulos claros). Default "#ffffff". */
  light?: string;
  /** Tamanho do logo central como fração do QR (0-0.3). Default 0.22. */
  logoPercent?: number;
  /** Cor sólida do badge do logo. Sobrepõe o gradient default se passado. */
  logoBg?: string;
  /**
   * Gradient pro badge — par [start, end]. Default usa as cores do brand-mark:
   * indigo `#6366f1` → violeta `#8b5cf6` (idênticas ao favicon do Simulador).
   * Ignorado quando `logoBg` é passado.
   */
  logoGradient?: [string, string];
  /** Cor do desenho do capelo. Default "#ffffff". */
  logoFg?: string;
}

/**
 * Gera o SVG do QR com o capelo no centro. A função é assíncrona porque
 * `qrcode.toString` é assíncrono — mas pequena (<5ms) na prática.
 *
 * @param text Conteúdo a codificar (Pix BRCode, URL, etc.)
 */
export async function generateQrWithLogoSvg(
  text: string,
  opts: QrWithLogoOptions = {}
): Promise<string> {
  const {
    margin = 1,
    dark = "#0f172a",
    light = "#ffffff",
    logoPercent = 0.22,
    logoBg,
    logoGradient = ["#6366f1", "#8b5cf6"],
    logoFg = "#ffffff"
  } = opts;

  // Error correction H tolera ~30% de obstrução — necessário com logo central.
  const rawSvg = await qrcode.toString(text, {
    type: "svg",
    errorCorrectionLevel: "H",
    margin,
    color: { dark, light }
  });

  // Extrai o viewBox pra saber as coordenadas do canvas SVG do QR.
  const viewBoxMatch = rawSvg.match(/viewBox="([\d.\s-]+)"/);
  if (!viewBoxMatch) return rawSvg; // fallback: sem logo se algo der errado
  const parts = viewBoxMatch[1].split(/\s+/).map(Number);
  if (parts.length !== 4) return rawSvg;
  const [, , w, h] = parts;

  // Cálculo do quadrado do logo no centro.
  const logoSize = Math.min(w, h) * logoPercent;
  const cx = w / 2;
  const cy = h / 2;
  const half = logoSize / 2;
  const x = cx - half;
  const y = cy - half;
  // Badge com 10% de "respiro" branco ao redor do logo
  const badgePad = logoSize * 0.12;
  const bx = x - badgePad;
  const by = y - badgePad;
  const bw = logoSize + badgePad * 2;
  const bh = logoSize + badgePad * 2;
  // ID único pro gradient pra não colidir caso múltiplos QRs estejam no DOM.
  const gradId = `qrl-${Math.random().toString(36).slice(2, 9)}`;
  const useGradient = !logoBg;
  const bgFill = useGradient ? `url(#${gradId})` : logoBg!;

  // Capelo (graduation cap) — paths do lucide GraduationCap em viewBox 24x24,
  // remapeado pro tamanho do logo.
  // Path original lucide: M22 10v6 / M2 10l10-5 10 5-10 5z / M6 12v5c0 1.66 4 3 6 3s6-1.34 6-3v-5
  const stroke = Math.max(1.2, logoSize * 0.08);
  const capelo = `
    <g transform="translate(${x} ${y}) scale(${logoSize / 24})"
       fill="none" stroke="${logoFg}" stroke-width="${(stroke * 24) / logoSize}"
       stroke-linecap="round" stroke-linejoin="round">
      <path d="M22 10v6"/>
      <path d="M2 10l10-5 10 5-10 5z"/>
      <path d="M6 12v5c0 1.66 4 3 6 3s6-1.34 6-3v-5"/>
    </g>`.trim();

  // Badge arredondado por trás do capelo. Gradient indigo→violeta diagonal
  // (mesmo eixo do brand-mark do Simulador), reproduzindo o favicon.
  const badgeRadius = bw * 0.18;
  const defs = useGradient
    ? `
      <defs>
        <linearGradient id="${gradId}" x1="${bx}" y1="${by}"
                        x2="${bx + bw}" y2="${by + bh}"
                        gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="${logoGradient[0]}"/>
          <stop offset="1" stop-color="${logoGradient[1]}"/>
        </linearGradient>
      </defs>`.trim()
    : "";
  const badge = `
    <rect x="${bx}" y="${by}" width="${bw}" height="${bh}"
          rx="${badgeRadius}" ry="${badgeRadius}"
          fill="${bgFill}"/>`.trim();

  // Injeta antes do </svg> de fechamento
  const injected = rawSvg.replace(
    /<\/svg>\s*$/,
    `${defs}${badge}${capelo}</svg>`
  );
  return injected;
}
